import type { UseApplyComponentActionType } from "@v1/hooks/useApplyComponentAction";
import type { Component, CustomPropDefinition } from "schemas/component";

import * as React from "react";

import { Group } from "@v1/components/common/designSystem/Group";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { getCustomComponentPropTypeEditorData } from "@v1/components/editor/customComponentProps";
import {
  getExtraActionsBasedOnComponentType,
  getSelectableOptionValues,
} from "@v1/components/editor/page/element-editor/components/modifiers/utils";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import { useStoreProductsFromDraftElement } from "@v1/hooks/useStoreProducts";
import { selectLocaleData } from "@v1/reducers/commerce-reducer";
import {
  selectAllTextComponentNames,
  selectComponentDataMapping,
  selectDraftComponent,
  selectDraftComponentContext,
  selectGetAttribute,
} from "@v1/reducers/core-reducer";
import { selectTemplateEditorStoreProduct } from "@v1/reducers/template-reducer";
import { useEditorSelector } from "@v1/store/store";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { Info } from "lucide-react";
import { getCustomPropDefinitions } from "replo-runtime/shared/utils/component";

const CustomPropModifier: React.FC = () => {
  const draftComponent = useEditorSelector(selectDraftComponent);

  const context = useEditorSelector(selectDraftComponentContext);
  const allTextComponents = useEditorSelector(selectAllTextComponentNames);

  const getAttribute = useEditorSelector(selectGetAttribute);
  const applyComponentAction = useApplyComponentAction();
  const {
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
  } = useEditorSelector(selectLocaleData);
  const { products } = useStoreProductsFromDraftElement();
  const { user } = useCurrentUser();
  const templateProduct =
    useEditorSelector(selectTemplateEditorStoreProduct) ?? null;
  if (!draftComponent) {
    return null;
  }

  const renderCustomProp = (customPropDefinition: CustomPropDefinition) => {
    const editorData = getCustomComponentPropTypeEditorData(
      customPropDefinition.type,
    );
    const { value } = getAttribute(
      draftComponent,
      `props.${customPropDefinition.id}`,
      {
        defaultValue: customPropDefinition.defaultValue,
      },
    );

    const onChange = (value: any) => {
      const actions: UseApplyComponentActionType[] = [
        {
          componentId: draftComponent.id,
          type: "setProps",
          value: { [customPropDefinition.id]: value },
        },
      ];

      const extraActionsBasedOnComponentType =
        getExtraActionsBasedOnComponentType(
          value,
          draftComponent,
          customPropDefinition,
        );

      applyComponentAction({
        type: "applyCompositeAction",
        value: [...actions, ...extraActionsBasedOnComponentType],
      });
    };

    const onDelete = () => {
      applyComponentAction({
        type: "deleteProps",
        propName: customPropDefinition.id,
      });
    };

    if (!editorData?.shouldRender(draftComponent)) {
      return null;
    }

    let isEnabled = true;
    if (
      customPropDefinition.isEnabled &&
      !customPropDefinition.isEnabled({ component: draftComponent })
    ) {
      isEnabled = false;
    }

    let shouldDisplay = true;
    if (
      customPropDefinition.shouldDisplay &&
      !customPropDefinition.shouldDisplay({
        component: draftComponent,
        userIsSuperuser: user?.isSuperuser ?? false,
      })
    ) {
      shouldDisplay = false;
    }

    const options = getSelectableOptionValues({
      customPropDefinition,
      draftComponent,
      context,
      productResolutionDependencies: {
        products,
        currencyCode: activeCurrency,
        language: activeLanguage,
        moneyFormat,
        moneyWithCurrencyFormat,
        templateProduct,
        isEditor: true,
        isShopifyProductsLoading: false,
      },
      allTextComponents,
    });

    const contentToRender = editorData?.render({
      component: draftComponent,
      products,
      value,
      customPropId: customPropDefinition.id,
      description: customPropDefinition.description ?? "",
      onChange,
      onDelete,
      options,
      attributeName: customPropDefinition.id,
      isEnabled,
      disabledDescription: customPropDefinition.disabledDescription,
      definition: customPropDefinition,
      context,
      defaultValue: customPropDefinition.defaultValue,
      disableDynamicData: customPropDefinition.disableDynamicData,
      templateProduct,
      placeholder: customPropDefinition.placeholder,
    });

    if (!shouldDisplay) {
      return null;
    }

    if (editorData.hasCustomWrapper) {
      return contentToRender;
    }

    return (
      <Group
        name={customPropDefinition.name}
        isDefaultOpen
        isCollapsible
        key={customPropDefinition.id}
      >
        <div className="w-full">{contentToRender}</div>
      </Group>
    );
  };

  const customProps = getCustomPropDefinitions(draftComponent);

  return (
    <div className="flex flex-col gap-4">
      {draftComponent.type === "tooltipContent" && (
        <TooltipContentInformation component={draftComponent} />
      )}
      {customProps
        .filter((definition) => definition.type !== "component")
        .map((customPropDefinition) => renderCustomProp(customPropDefinition))}
    </div>
  );
};

// TODO (REPL-16947 Gabe 2025-04-15): This should not be defined here. If we
// like this pattern of being able to show information about a component
// alongside prop modifiers then this should be configurable via component's
// config.
const TooltipContentInformation: React.FC<{ component: Component }> = ({
  component,
}) => {
  const componentData = useEditorSelector(selectComponentDataMapping)[
    component.id
  ];
  const setDraftElement = useSetDraftElement();
  return (
    <div className="bg-sheet text-muted flex gap-2 rounded-lg p-2 text-xs">
      <Info size={12} className="mt-0.5 shrink-0" />
      <div>
        Edit tooltip configurations on the parent component.
        <div className="text-primary underline">
          <DeprecatedButton
            variant="inherit"
            size="sm"
            onClick={() => {
              if (componentData?.parentId) {
                setDraftElement({ componentIds: [componentData.parentId] });
              }
            }}
          >
            <span>Go To Parent</span>
          </DeprecatedButton>
        </div>
      </div>
    </div>
  );
};

export default CustomPropModifier;
